function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { assert } from "@ember/debug";
import dayjs, { OpUnitType, QUnitType } from "dayjs";
import { IntlService } from "ember-intl";
import ConsultationPlannedJournalEvent from "./consultation-planned-journal-event";
import TreatmentHistoryChangedJournalEvent from "./treatment-history-changed-journal-event";
const CureDatePattern = /^cure\d*Date$/;
const PeriodDateNames = ["startDate", "endDate"];
const OkChimioDaysBeforeTraitement = 2;
const OkChimioDayAfterTraitement = 3;
/**
 * A Protocol can be a saved protocol created from the TreatmentHistoryChangedJournalEvent and having an uuid
 * But a Protocol can be created from a changeset and not be saved to the TreatmentHistoryChanged yet and not having a uuid
 */
export default class Protocol {
  static getCureDateId(index) {
    return `cure${index}Date`;
  }
  static new(args) {
    switch (args.protocol) {
      case "abemaciclib-verzenios":
      case "ribociclib-kisqali":
        {
          const startDate = args["treatment-start-date"];
          const endDate = dayjs(startDate).add(7, "months");
          return new Protocol({
            ...args,
            startDate,
            endDate
          });
        }
      default:
        return new Protocol(args);
    }
  }
  constructor(args) {
    _defineProperty(this, "uuid", void 0);
    _defineProperty(this, "type", void 0);
    _defineProperty(this, "types", void 0);
    _defineProperty(this, "startDate", void 0);
    _defineProperty(this, "endDate", void 0);
    _defineProperty(this, "surgery", void 0);
    _defineProperty(this, "paused", void 0);
    assert("A protocol must have a protocol property", Boolean(args["protocol"]));
    Object.assign(this, args);
  }
  get treatmentProtocolType() {
    if (this.surgery || this["cureDate"]) return "surgery";else if (PeriodDateNames.some(n => this[n])) return "period";
    return "cure";
  }
  getConsultations(treatmentHistory) {
    if (this.uuid) return [];
    return treatmentHistory.consultationPlannedJournalEvents.filter(consult => consult.originConsultationIdentifier.includes(this.uuid));
  }
  getProtocolLabel(intl) {
    if (this.protocol == "default-other-follow-up-events" && this.type) {
      return intl.t(`other-events.${this.type}`);
    }
    return intl.t(`treatment-protocol.${this.protocol}`);
  }

  /**
   * Active means that there is a cure in the future
   */
  get isActive() {
    switch (this.treatmentProtocolType) {
      case "period":
        // startDate or endDate in the future
        // or startDate in the past but no endDate
        return PeriodDateNames.some(n => dayjs().isBefore(this[n])) || Boolean(this.startDate && !this.endDate);
      case "surgery":
        return Boolean(this.surgery || this["cureDate"]);
      default:
        return Boolean(this.nextCureDate);
    }
  }
  get isFinished() {
    return this.allDatesFilled && !this.isActive;
  }
  get isPaused() {
    return Boolean(this.uuid) && this.cureDateValues.some(d => d === null) || Boolean(this.paused);
  }
  get canBePaused() {
    return this.isActive && !this.isPaused && this.allDatesFilled;
  }
  get isDefaultProtocol() {
    return this.protocol.includes("default");
  }
  get startProtocolDate() {
    return this[Protocol.getCureDateId(1)] || this.startDate;
  }

  /**
   * Return the next important date not depending on the protocol type
   * Could be in the past for surgeries
   */
  get nextActiveOrImportantDate() {
    switch (this.treatmentProtocolType) {
      case "period":
        return dayjs().isAfter(this.startProtocolDate) ? this.endDate : this.startProtocolDate;
      case "surgery":
        return this.surgery || this["cureDate"];
      default:
        return this.nextCureDate;
    }
  }
  get nextCureIndex() {
    const today = dayjs();
    let nextCureIndex;
    let nearestCureDiff = Infinity;
    this.cureDates.forEach((cureDate, index) => {
      const diff = dayjs(cureDate).diff(today);
      if (diff > 0 && diff < nearestCureDiff) {
        nextCureIndex = index + 1;
        nearestCureDiff = diff;
      }
    });
    return nextCureIndex;
  }
  get nextCureKey() {
    return this.nextCureIndex ? Protocol.getCureDateId(this.nextCureIndex) : undefined;
  }
  get nextCureDate() {
    return this.nextCureIndex ? this.getCureDate(this.nextCureIndex) : undefined;
  }
  get lastCureIndex() {
    const today = dayjs();
    let nextCureIndex;
    let nearestCureDiff = -Infinity;
    this.cureDates.forEach((cureDate, index) => {
      const diff = dayjs(cureDate).diff(today);
      if (diff < 0 && diff > nearestCureDiff) {
        nextCureIndex = index + 1;
        nearestCureDiff = diff;
      }
    });
    return nextCureIndex;
  }
  get lastCureKey() {
    return this.lastCureIndex ? Protocol.getCureDateId(this.lastCureIndex) : undefined;
  }
  get lastCureDate() {
    return this.lastCureIndex ? this.getCureDate(this.lastCureIndex) : undefined;
  }
  get allDatesFilled() {
    return this.cureDates.length == this.numberOfCures;
  }
  get cureDates() {
    return this.cureDateValues.filter(d => Boolean(d));
  }
  get isInOkChimioValidationPeriod() {
    return Boolean(this.cureKeyHappeningInLessThan(OkChimioDaysBeforeTraitement, "days")) || Boolean(this.cureKeyHappenedLessThan(OkChimioDayAfterTraitement, "days"));
  }
  get cureKeyInOkChimioValidationPeriod() {
    return this.cureKeyHappeningInLessThan(OkChimioDaysBeforeTraitement, "days") || this.cureKeyHappenedLessThan(OkChimioDayAfterTraitement, "days");
  }
  cureKeyHappeningInLessThan(numberOf, unit) {
    const nextCureKey = this.nextCureKey;
    if (!nextCureKey) return undefined;
    const nextCureDate = this[this.nextCureKey];
    if (Boolean(nextCureDate) && dayjs(nextCureDate).diff(dayjs(), unit) <= numberOf) return nextCureKey;
    return undefined;
  }
  cureKeyHappenedLessThan(numberOf, unit) {
    const lastCureKey = this.lastCureKey;
    if (!lastCureKey) return undefined;
    const lastCureDate = this[this.lastCureKey];
    if (Boolean(lastCureDate) && dayjs().diff(lastCureDate, unit) <= numberOf) return lastCureKey;
    return undefined;
  }
  async pauseProtocol(treatmentHistory) {
    this.removeCureInTheFuture();
    this.paused = true;
    await treatmentHistory.save();
    await treatmentHistory.clearAndFetchAssociatedTaskAndConsultation();
  }
  removeCureInTheFuture() {
    const now = dayjs().startOf("day");
    for (let i = 1; i <= this.numberOfCures; i++) {
      const date = dayjs(this.getCureDate(i)).endOf("day");
      if (date.isAfter(now)) {
        this.setCureDate(i, null);
      }
    }
  }
  setCureDate(index, date) {
    this[Protocol.getCureDateId(index)] = date;
  }
  getCureDate(index) {
    return this[Protocol.getCureDateId(index)];
  }
  get numberOfCures() {
    return this.cureDateValues.length;
  }

  /**
   * It returns every cureDate values even the ones set to null
   * It could be usefull for paused treatment
   */
  get cureDateValues() {
    return Object.entries(this).map(([key, value]) => CureDatePattern.test(key) ? value : undefined).filter(d => d !== undefined);
  }
}